import type {
  BankReconciliationStatusDto,
  BankReconciliationRunStatusDto,
  LateAfterLeaveListDto,
  LateAfterLeaveListQueryDto,
  ReconciliationKpiSnapshotDto,
  ResolveReconciliationCandidateRequestDto,
  ResolveReconciliationCandidateResponseDto,
  RunBankReconciliationNowDto,
} from './reconciliation.types.js';
import type { ApiClientCore } from './apiClient.core.js';
import type { ApiResponse } from './apiClient.types.js';

export const reconciliationMethods = {
  /**
   * Bank transfer reconciliation worker — canonical `/api/v1/admin/workers/...`
   * (same-origin Vite/Caddy proxy; legacy root `/admin/workers/...` remains on API for curl).
   */
  async getBankTransferReconciliationAdminStatus(
    this: ApiClientCore,
    runId?: string,
    requestOptions?: RequestInit
  ): Promise<ApiResponse<BankReconciliationStatusDto>> {
    const suffix = runId ? `?runId=${encodeURIComponent(runId)}` : '';
    return this.get(
      `/api/v1/admin/workers/bank-transfer-reconciliation/status${suffix}`,
      true,
      { kind: 'default' },
      requestOptions
    );
  },

  async runBankTransferReconciliationHourlyTickNow(this: ApiClientCore): Promise<ApiResponse<RunBankReconciliationNowDto>> {
    return this.post('/api/v1/admin/workers/bank-transfer-reconciliation/run-hourly-tick-now', {}, true);
  },

  /**
   * Run-status endpoint (Reconciliation Final Freeze WS1).
   * Path: `/api/v1/admin/reconciliation/runs/:runId`.
   *
   * Local-read guarantee: backend serves this from durable run-state DB row
   * and in-memory expired markers only — never invokes the FIO adapter.
   */
  async getBankTransferReconciliationRunStatus(
    this: ApiClientCore,
    runId: string,
    requestOptions?: RequestInit
  ): Promise<ApiResponse<BankReconciliationRunStatusDto>> {
    return this.get(
      `/api/v1/admin/reconciliation/runs/${encodeURIComponent(runId)}`,
      true,
      { kind: 'default' },
      requestOptions
    );
  },

  async getBankTransferReconciliationKpiSnapshot(this: ApiClientCore): Promise<ApiResponse<ReconciliationKpiSnapshotDto>> {
    return this.get('/api/v1/admin/workers/bank-transfer-reconciliation/kpi', true);
  },

  /**
   * Resolve a refund candidate (Reconciliation Final Freeze WS3).
   * Path: `/api/v1/admin/reconciliation/candidates/:candidateId/resolve`.
   * Idempotency key is sent both in body and `Idempotency-Key` header.
   */
  async resolveBankTransferReconciliationCandidate(
    this: ApiClientCore,
    request: ResolveReconciliationCandidateRequestDto
  ): Promise<ApiResponse<ResolveReconciliationCandidateResponseDto>> {
    return this.post(
      `/api/v1/admin/reconciliation/candidates/${encodeURIComponent(request.candidateId)}/resolve`,
      {
        idempotencyKey: request.idempotencyKey,
        decision: request.decision,
        reasonCode: request.reasonCode,
        note: request.note ?? null,
      },
      true,
      { kind: 'default' },
      {
        headers: { 'Idempotency-Key': request.idempotencyKey },
      }
    );
  },

  async listBankTransferReconciliationLateAfterLeave(
    this: ApiClientCore,
    query: LateAfterLeaveListQueryDto
  ): Promise<ApiResponse<LateAfterLeaveListDto>> {
    const params = new URLSearchParams();
    params.set('limit', String(query.limit));
    params.set('offset', String(query.offset));
    if (query.onlyUnresolvedRefund === true) {
      params.set('onlyUnresolvedRefund', 'true');
    }
    if (query.tenantId !== undefined && Number.isFinite(query.tenantId)) {
      params.set('tenantId', String(query.tenantId));
    }
    if (query.providerId !== undefined && query.providerId.trim() !== '') {
      params.set('providerId', query.providerId.trim());
    }
    const suffix = params.toString();
    return this.get(
      `/api/v1/admin/workers/bank-transfer-reconciliation/late-after-leave${suffix ? `?${suffix}` : ''}`,
      true
    );
  },
};
